import { cardImageSrc, cardBackSrc, CARD_RATIO } from "@/lib/artAssets";
import { S } from "@/lib/sound";

// Pile = a 900x1440 card at 66px wide, so the card backs fill it exactly.
const PILE = { width: 66, height: Math.round(66 * CARD_RATIO), borderRadius: 8 };
// Deck and discard share one fixed box: the pile plus room for the two under-cards peeking out 3px / 6px.
export const PILE_BOX = { width: PILE.width + 6, height: PILE.height + 6 };

function PileLabel({ label, count, testid }) {
  return (
    <span className="flex flex-col items-center text-center" style={{ marginTop: 3, lineHeight: "11px", width: 64 }}>
      <span style={{ fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ink-4)" }}>{label}</span>
      <span data-testid={testid} style={{ fontSize: 12.5, lineHeight: "15px", fontWeight: 700, color: "var(--ink)" }}>({count})</span>
    </span>
  );
}

// Card-back colours: build = sea, action = clay. /assets/cards/card_back_{kind}.png is used when it exists.
export const BACK_COLORS = { build: { face: "var(--sea)", under1: "#20414F", under2: "#1B3846" }, action: { face: "#8E4A35", under1: "#7A3F2D", under2: "#663526" } };

// Plain coloured back with an optional art image laid over it (hidden if the file is missing).
// `bleed` lets the art cover the frame's border so the 900x1440 back fills the pile edge to edge.
export function BackArt({ kind = "build", radius = 4, bleed = 0 }) {
  return (
    <img decoding="async"
      src={cardBackSrc(kind)}
      alt=""
      aria-hidden="true"
      draggable={false}
      onError={(e) => { e.currentTarget.style.display = "none"; }}
      className="absolute select-none"
      style={{ inset: -bleed, width: `calc(100% + ${2 * bleed}px)`, height: `calc(100% + ${2 * bleed}px)`, maxWidth: "none", objectFit: "cover", borderRadius: radius }}
    />
  );
}

function CardBack({ kind = "build" }) {
  return (
    <span className="absolute inset-0 flex items-center justify-center overflow-hidden" style={{ ...PILE, background: BACK_COLORS[kind].face, border: "3px solid var(--card)", boxSizing: "border-box" }}>
      <span className="absolute" style={{ inset: 5, borderRadius: 4, border: "1px solid rgba(251,250,247,.35)" }} />
      <svg width="34" height="20" viewBox="0 0 34 20" fill="none" stroke="rgba(251,250,247,.7)" strokeWidth="1.2" strokeLinecap="round" aria-hidden="true">
        <path d="M1 4 Q 5.25 1, 9.5 4 T 18 4 T 26.5 4 T 33 4" />
        <path d="M1 10 Q 5.25 7, 9.5 10 T 18 10 T 26.5 10 T 33 10" />
        <path d="M1 16 Q 5.25 13, 9.5 16 T 18 16 T 26.5 16 T 33 16" />
      </svg>
      <BackArt kind={kind} radius={PILE.borderRadius} bleed={3} />
    </span>
  );
}

// Face-down build deck: refills the market; during a pick it can be clicked to draw the top card
// blind ("Draw blind"; PAY UPFRONT: the card is shown to you, then paid). reason: why it can't
// be drawn during the pick (greyed, shown on hover / click).
function TakeLabel({ testid, children }) {
  return (
    <span
      data-testid={testid}
      className="absolute flex items-center justify-center text-center"
      style={{ left: 5, right: 5, top: "50%", transform: "translateY(-50%)", padding: "4px 2px", borderRadius: 5, background: "rgba(251,250,247,0.94)", color: "var(--sea)", fontSize: 11, lineHeight: "13px", fontWeight: 700, boxShadow: "0 1px 3px rgba(0,0,0,.18)", zIndex: 1 }}
    >
      {children}
    </span>
  );
}

export function DeckPile({ count, enabled = false, reason = null, onTake, onBlocked }) {
  const inner = (
    <>
      {count > 2 && <span className="absolute" style={{ ...PILE, left: 6, top: 6, background: BACK_COLORS.build.under2 }} />}
      {count > 1 && <span className="absolute" style={{ ...PILE, left: 3, top: 3, background: BACK_COLORS.build.under1 }} />}
      {count > 0 ? <CardBack kind="build" /> : <span className="absolute left-0 top-0" style={{ ...PILE, border: "1.5px dashed #B9B3A6" }} />}
      {enabled && <TakeLabel testid="deck-take-label">Draw blind</TakeLabel>}
    </>
  );
  return (
    <div className="flex flex-col items-center" style={{ width: PILE_BOX.width }} title={enabled ? "Draw the top build card blind: only you see it, then you pay its price (can't afford it: it is discarded)" : reason || "Build deck: refills the market; during a pick you may draw its top card blind"}>
      {enabled ? (
        <button
          type="button"
          data-testid="deck-pile"
          data-enabled="true"
          aria-label={`Draw blind: the top build card, face down (${count} in the deck)`}
          onClick={() => {
            S.cardDraw();
            onTake?.();
          }}
          className="relative shrink-0 p-0 border-0 bg-transparent list-row-lift cursor-pointer"
          style={{ ...PILE_BOX, borderRadius: PILE.borderRadius, boxShadow: "0 0 0 2px var(--sea)" }}
        >
          {inner}
        </button>
      ) : reason ? (
        <button
          type="button"
          data-testid="deck-pile"
          data-enabled="false"
          aria-disabled="true"
          aria-label={`Build deck, ${count} cards face down. ${reason}`}
          onClick={() => onBlocked?.(reason)}
          className="relative shrink-0 p-0 border-0 bg-transparent cursor-not-allowed"
          style={{ ...PILE_BOX, opacity: 0.5, filter: "grayscale(0.6)" }}
        >
          {inner}
        </button>
      ) : (
        <div data-testid="deck-pile" data-enabled="false" aria-label={`Build deck, ${count} cards face down`} className="relative shrink-0" style={PILE_BOX}>
          {inner}
        </div>
      )}
      <PileLabel label="Build card deck" count={count} testid="deck-count" />
    </div>
  );
}

// Face-down action pile: click to take the top card (when allowed).
// reason: why it can't be taken now (greyed pile, shown on hover and under it).
// PAY UPFRONT: taking one costs `price` (3), paid at once ("Take (3)" on the pile).
export function ActionPile({ count, discardCount = 0, enabled, onTake, reason = null, onBlocked, price = 3 }) {
  return (
    <div className="flex flex-col items-center" style={{ width: PILE_BOX.width }}>
      <div className="relative shrink-0" style={PILE_BOX}>
        <button
          type="button"
          data-testid="action-pile"
          aria-label={`Action pile, ${count} cards face down`}
          aria-disabled={!enabled}
          disabled={!enabled && !reason}
          onClick={() => {
            if (!enabled) {
              onBlocked?.(reason);
              return;
            }
            S.cardDraw();
            onTake();
          }}
          className={`relative p-0 border-0 bg-transparent ${enabled ? "list-row-lift cursor-pointer" : "cursor-not-allowed"}`}
          style={{ width: PILE.width, height: PILE.height, opacity: enabled || !reason ? 1 : 0.5, filter: enabled || !reason ? "none" : "grayscale(0.6)" }}
          data-enabled={enabled ? "true" : "false"}
          title={enabled ? `Take an action card: pay ${price} now (once per turn)` : reason || `Action pile · ${discardCount} in the action discard`}
        >
          {count > 2 && <span className="absolute" style={{ ...PILE, left: 6, top: 6, background: BACK_COLORS.action.under2 }} />}
          {count > 1 && <span className="absolute" style={{ ...PILE, left: 3, top: 3, background: BACK_COLORS.action.under1 }} />}
          {count > 0 ? <CardBack kind="action" /> : <span className="absolute inset-0" style={{ ...PILE, border: "1.5px dashed #B9B3A6" }} />}
          {enabled && <TakeLabel testid="action-take-label">Take ({price})</TakeLabel>}
        </button>
      </div>
      <PileLabel label="Action card deck" count={count} testid="action-pile-count" />
    </div>
  );
}

// Discard: dashed "Empty", or the top card's PNG with offset edges behind it.
export function DiscardPile({ pile, onOpen }) {
  const top = pile[pile.length - 1];
  return (
    <div data-testid="discard-pile" className="flex flex-col items-center" style={{ width: PILE_BOX.width }} title="Played and discarded cards">
      <div data-testid="discard-pile-box" className="relative shrink-0" style={PILE_BOX}>
      {top ? (
        <button type="button" data-testid="discard-pile-top" data-card-key={top.key} onClick={() => onOpen(top)} aria-label={`Discard pile, top card ${top.name}`} className="relative p-0 border-0 bg-transparent list-row-lift" style={{ width: PILE.width, height: PILE.height }}>
          {pile.length > 2 && <span className="absolute" style={{ ...PILE, left: 6, top: 6, background: "#D3CDC1" }} />}
          {pile.length > 1 && <span className="absolute" style={{ ...PILE, left: 3, top: 3, background: "#E0DBD0", border: "1px solid var(--card-border)" }} />}
          <img decoding="async" src={cardImageSrc(top.key)} alt={top.name} draggable={false} className="absolute left-0 top-0 select-none" style={{ ...PILE, objectFit: "contain", background: "var(--card)" }} />
        </button>
      ) : (
        <div data-testid="discard-pile-top" className="flex items-center justify-center" style={{ ...PILE, border: "1.5px dashed #B9B3A6", fontSize: 11, color: "var(--ink-5)" }}>
          Empty
        </div>
      )}
      </div>
      <PileLabel label="Discard pile" count={pile.length} testid="discard-pile-count" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// WEATHER and GLOBAL decks (v3 step 4): a face-down draw pile with its card count next to the
// last card drawn, face up. Nobody takes from these piles: one card of each is drawn by the app
// at the end of every round. `label`: "Weather" / "Global"; kind: the card back ("weather" | "global");
// last: { src, name, detail } of the card resolved last (null = nothing yet).
// ---------------------------------------------------------------------------
const SMALL = { width: 52, height: Math.round(52 * CARD_RATIO), borderRadius: 6 };
const SMALL_BOX = { width: SMALL.width + 4, height: SMALL.height + 4 };

export function EventDeck({ kind, label, count, last = null, testid, title, dim = false }) {
  return (
    <div data-testid={`${testid}-deck`} className="focus-region flex flex-col items-center" style={{ width: 2 * SMALL_BOX.width + 8, opacity: dim ? 0.5 : 1 }} title={title}>
      <div className="flex items-start" style={{ gap: 8 }}>
        <div data-testid={`${testid}-pile`} className="relative shrink-0" style={SMALL_BOX} aria-label={`${label} deck, ${count} cards face down`}>
          {count > 1 && <span className="absolute" style={{ ...SMALL, left: 3, top: 3, background: kind === "global" ? "#5A3B2E" : "#20414F" }} />}
          {count > 0 ? (
            <span className="absolute left-0 top-0 overflow-hidden" style={{ ...SMALL, background: kind === "global" ? "#8E4A35" : "var(--sea)" }}>
              <BackArt kind={kind} radius={SMALL.borderRadius} />
            </span>
          ) : (
            <span className="absolute left-0 top-0" style={{ ...SMALL, border: "1.5px dashed #B9B3A6" }} />
          )}
        </div>
        <div data-testid={`${testid}-last`} className="relative shrink-0" style={SMALL_BOX} title={last ? `${last.name}${last.detail ? ` — ${last.detail}` : ""}` : "Nothing drawn yet"}>
          {last?.src ? (
            <img decoding="async" src={last.src} alt={last.name} draggable={false} className="absolute left-0 top-0 select-none" style={{ ...SMALL, objectFit: "cover", boxShadow: "0 1px 4px rgba(36,40,38,.3)" }} />
          ) : (
            <span className="absolute left-0 top-0 flex items-center justify-center" style={{ ...SMALL, border: "1.5px dashed #B9B3A6", fontSize: 10, color: "var(--ink-5)" }}>none yet</span>
          )}
        </div>
      </div>
      <span className="flex flex-col items-center text-center" style={{ marginTop: 3, lineHeight: "11px", width: 2 * SMALL_BOX.width + 8 }}>
        <span style={{ fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ink-4)" }}>{label} deck · last card</span>
        <span data-testid={`${testid}-count`} style={{ fontSize: 12, lineHeight: "15px", fontWeight: 700, color: "var(--ink)" }}>({count})</span>
      </span>
    </div>
  );
}
